import type { Citation } from '~/common/componentMapper/PdfViewer/types';

type PdfInput = string | ArrayBuffer | Uint8Array<ArrayBufferLike>;

const defaultSettings = {
  ignoreEncryption: true,
  password: '',
};

export const createPdfBlob = async (pdfFile: PdfInput) => {
  const { PDFDocument } = await import('@cantoo/pdf-lib');
  const pdfDoc = await PDFDocument.load(pdfFile, defaultSettings);
  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes as any], { type: 'application/pdf' });
};

export const createBlobUrl = async (pdfBuffer: PdfInput) => {
  const blob = await createPdfBlob(pdfBuffer);

  return URL.createObjectURL(blob);
};

export const getPdfPageCount = async (pdfFile: PdfInput) => {
  const { PDFDocument } = await import('@cantoo/pdf-lib');
  const pdfDoc = await PDFDocument.load(pdfFile, defaultSettings);
  return pdfDoc.getPageCount();
};

type BoundingBox = NonNullable<Citation['boundingBoxes']>[number];

/**
 * Draws citation bounding boxes directly onto the PDF so highlights survive
 * in a native (iframe) viewer. Boxes are normalized 0-1 with a top-left origin.
 */
export const createHighlightedPdfBlob = async (
  pdfFile: PdfInput,
  boundingBoxes: Array<BoundingBox>,
  { filled = false }: { filled?: boolean } = {},
) => {
  const { PDFDocument, rgb } = await import('@cantoo/pdf-lib');
  const pdfDoc = await PDFDocument.load(pdfFile, defaultSettings);
  const pages = pdfDoc.getPages();
  const highlightColor = rgb(1, 0, 0);

  boundingBoxes.forEach((box) => {
    const page = pages[(box.pageNumber ?? 1) - 1];
    if (!page) return;

    const xs = [box.x1, box.x2, box.x3, box.x4].filter(
      (value): value is number => typeof value === 'number',
    );
    const ys = [box.y1, box.y2, box.y3, box.y4].filter(
      (value): value is number => typeof value === 'number',
    );
    if (xs.length === 0 || ys.length === 0) return;

    const { width, height } = page.getSize();
    const lowestX = Math.min(...xs);
    const highestX = Math.max(...xs);
    const lowestY = Math.min(...ys);
    const highestY = Math.max(...ys);

    const padding = 2;

    page.drawRectangle({
      x: lowestX * width - padding,
      // Convert top-left normalized origin to pdf-lib's bottom-left origin.
      y: height - highestY * height - padding,
      width: (highestX - lowestX) * width + padding * 2,
      height: (highestY - lowestY) * height + padding * 2,
      color: filled ? highlightColor : undefined,
      opacity: filled ? 0.3 : undefined,
      borderColor: filled ? undefined : highlightColor,
      borderWidth: filled ? undefined : 1.5,
    });
  });

  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes as any], { type: 'application/pdf' });
};

export function setActiveCitation(citation: Citation) {
  window.postMessage(
    {
      type: 'pdfCitation',
      payload: [citation],
    },
    globalThis.location.origin,
  );
}
